import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatMoney, getCurrencyCode } from '@/shared/lib/format/money';
import { useCan } from '@/shared/hooks/useCan';
import { useProfileQuery } from '@/features/profile/api/useProfileQueries';
import {
  useWalletStatsQuery,
  useTransactionsQuery,
  useSettlementsQuery,
  useDownloadCsvMutation,
  useInvoiceMutation,
} from '../../api/useWalletQueries';
import { DepositModal } from '../../components/DepositModal';
import { WithdrawModal } from '../../components/WithdrawModal';
import { QndtTransferModal } from '../../components/QndtTransferModal';
import { useWalletChartData } from './useWalletChartData';
import { BalanceCard } from './sections/BalanceCard';
import { MetricsGrid, type WalletMetric } from './sections/MetricsGrid';
import { WalletChart } from './sections/WalletChart';
import { TransactionsPanel } from './sections/TransactionsPanel';
import type { Settlement, Txn, WalletData } from './WalletPage.types';

export default function WalletPage() {
  const currency = getCurrencyCode();
  const fmt = (v?: number) => (v != null ? formatMoney(v) : `${currency} 0.00`);

  const canDeposit = useCan('add-deposit');
  const canWithdraw = useCan('withdraw-amount');
  const canQndt = useCan('qndt-transfer');
  // const canQndt = useCan('transfer-qndt');
  const profileQ = useProfileQuery();
  const isVerified =
    String(profileQ.data?.data?.status || '').toUpperCase() === 'VERIFIED';

  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [qndtOpen, setQndtOpen] = useState(false);

  const statsQ = useWalletStatsQuery();
  const w: WalletData = statsQ.data?.data || {};

  const requireVerified = (action: () => void) => {
    if (!isVerified) {
      toast.error('Your account is not verified yet');
      return;
    }
    action();
  };

  const chart = useWalletChartData();

  const PAGE_SIZE = 10;
  const [tab, setTab] = useState<'transactions' | 'withdrawals'>('transactions');
  const [txnPage, setTxnPage] = useState(1);
  const [settPage, setSettPage] = useState(1);
  const [reason, setReason] = useState('');

  const txnQ = useTransactionsQuery({
    page: txnPage,
    limit: PAGE_SIZE,
    ...(reason && { reason }),
  });
  const settQ = useSettlementsQuery({ page: settPage, limit: PAGE_SIZE });
  const csvMut = useDownloadCsvMutation();
  const invoiceMut = useInvoiceMutation();
  const invoiceLoadingId = invoiceMut.isPending
    ? (invoiceMut.variables as { id: string } | undefined)?.id
    : undefined;

  const txns: Txn[] = txnQ.data?.items ?? [];
  const txnTotal = txnQ.data?.total ?? txns.length;
  const txnTotalPages = Math.max(1, txnQ.data?.totalPages ?? 1);

  const settlements: Settlement[] = settQ.data?.items ?? [];
  const settTotal = settQ.data?.total ?? settlements.length;
  const settTotalPages = Math.max(1, settQ.data?.totalPages ?? 1);

  if (statsQ.isPending) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--dash-brand)]" />
      </div>
    );
  }

  const metrics: WalletMetric[] = [
    {
      label: 'Available Top Up',
      value: fmt(w.availableTopUp),
      hint: `Total top up · ${fmt(w.totalTopUpAmount)}`,
      note: 'Non-withdrawable',
    },
    {
      label: 'Current Balance',
      value: fmt(w.currentBalance),
      hint: `Total deposited · ${fmt(w.totalDeposited)}`,
    },
    { label: 'Total Spent', value: fmt(w.totalSpent) },
    { label: 'Total Withdrawn', value: fmt(w.withdrawals) },
    { label: 'Total Refunds', value: fmt(w.refunds) },
    {
      label: 'Available QNDT',
      value: fmt(w.availableQndt),
      hint: `Total QNDT · ${fmt(w.quantumPoints)}`,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 pb-12 pt-6 sm:px-6">
      <div className="mb-6 text-left">
        <h1 className="text-xl font-bold text-[var(--dash-ink)]">Wallet</h1>
        <p className="mt-0.5 text-sm text-[var(--dash-muted)]">
          Balance, usage, and transaction history
        </p>
      </div>

      <BalanceCard
        formattedBalance={fmt(w.availableBalance)}
        availableQndt={Number(w.availableQndt ?? 0)}
        isVerified={isVerified}
        canDeposit={canDeposit}
        canWithdraw={canWithdraw}
        canQndt={canQndt}
        onDeposit={() => requireVerified(() => setDepositOpen(true))}
        onWithdraw={() => requireVerified(() => setWithdrawOpen(true))}
        onQndtTransfer={() => requireVerified(() => setQndtOpen(true))}
      />

      <MetricsGrid metrics={metrics} />

      <WalletChart
        chartData={chart.chartData}
        isLoading={chart.isChartLoading}
        chartReason={chart.chartReason}
        onReasonChange={chart.setChartReason}
        chartStart={chart.chartStart}
        onStartChange={chart.setChartStart}
        chartEnd={chart.chartEnd}
        onEndChange={chart.setChartEnd}
      />

      <TransactionsPanel
        tab={tab}
        onTabChange={setTab}
        reason={reason}
        onReasonChange={(r) => {
          setReason(r);
          setTxnPage(1);
        }}
        onExportCsv={() => csvMut.mutate()}
        isExporting={csvMut.isPending}
        txnPage={txnPage}
        onTxnPageChange={setTxnPage}
        settPage={settPage}
        onSettPageChange={setSettPage}
        pageSize={PAGE_SIZE}
        isTxnsLoading={txnQ.isPending}
        isTxnsFetching={txnQ.isFetching}
        txns={txns}
        txnTotal={txnTotal}
        txnTotalPages={txnTotalPages}
        isSettlementsLoading={settQ.isPending}
        isSettlementsFetching={settQ.isFetching}
        settlements={settlements}
        settTotal={settTotal}
        settlementTotalPages={settTotalPages}
        invoiceLoadingId={invoiceLoadingId}
        onDownloadTxnInvoice={(t) =>
          invoiceMut.mutate({
            id: t._id,
            type: t.reason?.trim()?.toLowerCase() || '',
          })
        }
        onViewSettlementInvoice={(s) => {
          if (s.wallet?._id) {
            invoiceMut.mutate({ id: s.wallet._id, type: 'settlement' });
          }
        }}
      />

      <DepositModal
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        availableBalance={w.availableBalance}
      />
      <WithdrawModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        currentBalance={w.currentBalance}
      />
      <QndtTransferModal
        open={qndtOpen}
        onClose={() => setQndtOpen(false)}
        availableBalance={w.availableQndt ?? w.availableBalance}
      />
    </div>
  );
}
